import { useState, useEffect, useCallback } from "react";
import {
  Users, UserPlus, Trash2, Shield, User, Loader2,
  Edit3, Check, X, Mail, Search,
  Plus, Link2, Unlink, Eye, EyeOff
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import StatCard from "./StatCard";

const USER_PERMISSIONS = [
  { key: "can_create_emails", label: "Créer des emails", description: "Peut générer de nouveaux emails temporaires" },
  { key: "can_reply", label: "Répondre aux emails", description: "Peut envoyer des réponses depuis ses emails" },
  { key: "can_permanent", label: "Emails permanents", description: "Peut créer des emails sans expiration" },
  { key: "can_custom_address", label: "Adresse personnalisée", description: "Peut choisir le nom de son adresse email" },
];

const ADMIN_PERMISSIONS = [
  { key: "can_manage_users", label: "Gérer les utilisateurs", description: "Peut créer, modifier et supprimer des utilisateurs" },
  { key: "can_edit_config", label: "Modifier la configuration", description: "Peut changer les paramètres du site (durées, domaines, maintenance...)" },
  { key: "can_delete_emails", label: "Supprimer des emails", description: "Peut supprimer les emails et messages du dashboard" },
  { key: "can_customize", label: "Personnalisation", description: "Peut modifier l'apparence (couleurs, textes, footer)" },
];

const ROLE_CONFIG = {
  super_admin: { label: "Super Admin", color: "bg-red-500/20 text-red-400 border-red-500/30" },
  admin: { label: "Admin", color: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
  user: { label: "Utilisateur", color: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30" },
};

export default function UsersContent({
  users, usersStats, isLoadingUsers,
  fetchUsers, fetchUsersStats,
  createUser, updateUser, deleteUser,
  assignEmailToUser, unassignEmailFromUser, searchEmailsForAssign, fetchEmailsByIds,
  currentAdminRole,
}) {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState("user");
  const [isCreating, setIsCreating] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editUsername, setEditUsername] = useState("");
  const [editRole, setEditRole] = useState("");
  const [editPassword, setEditPassword] = useState("");
  const [editPermissions, setEditPermissions] = useState({});
  const [expandedUser, setExpandedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [managingEmailsFor, setManagingEmailsFor] = useState(null);
  const [emailSearch, setEmailSearch] = useState("");
  const [emailResults, setEmailResults] = useState([]);
  const [isSearchingEmails, setIsSearchingEmails] = useState(false);
  const [assignedEmails, setAssignedEmails] = useState([]);
  const [isLoadingAssigned, setIsLoadingAssigned] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchUsersStats();
    // Auto-refresh every 30s for online status
    const interval = setInterval(() => {
      fetchUsers();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchUsers, fetchUsersStats]);

  const handleCreate = async () => {
    if (!newUsername.trim() || !newPassword.trim()) return;
    setIsCreating(true);
    const success = await createUser(newUsername, newPassword, newRole);
    setIsCreating(false);
    if (success) {
      setNewUsername("");
      setNewPassword("");
      setNewRole("user");
      setShowNewPassword(false);
      setShowCreateForm(false);
    }
  };

  const handleUpdate = async (userId) => {
    const updates = {};
    if (editUsername.trim()) updates.username = editUsername;
    if (editPassword.trim()) updates.password = editPassword;
    if (editRole) updates.role = editRole;
    updates.permissions = editPermissions;
    await updateUser(userId, updates);
    setEditingUser(null);
    setEditPassword("");
    setShowEditPassword(false);
  };

  const handleDelete = async (userId, username) => {
    if (!window.confirm(`Supprimer l'utilisateur "${username}" ?`)) return;
    await deleteUser(userId);
  };

  const openEmailManager = useCallback(async (user) => {
    setManagingEmailsFor(user.id);
    setEmailSearch("");
    setEmailResults([]);
    // Load currently assigned emails by their IDs directly
    if (user.email_ids?.length > 0) {
      setIsLoadingAssigned(true);
      const emails = await fetchEmailsByIds(user.email_ids);
      setAssignedEmails(emails);
      setIsLoadingAssigned(false);
    } else {
      setAssignedEmails([]);
    }
  }, [fetchEmailsByIds]);

  const handleEmailSearch = useCallback(async (query) => {
    setEmailSearch(query);
    if (!query.trim()) {
      setEmailResults([]);
      return;
    }
    setIsSearchingEmails(true);
    const results = await searchEmailsForAssign(query);
    setEmailResults(results);
    setIsSearchingEmails(false);
  }, [searchEmailsForAssign]);

  const handleAssign = async (userId, email) => {
    const success = await assignEmailToUser(userId, email.id);
    if (success) {
      setAssignedEmails(prev => [...prev, email]);
      setEmailResults(prev => prev.filter(e => e.id !== email.id));
      fetchUsers();
    }
  };

  const handleUnassign = async (userId, emailId) => {
    const success = await unassignEmailFromUser(userId, emailId);
    if (success) {
      setAssignedEmails(prev => prev.filter(e => e.id !== emailId));
      fetchUsers();
    }
  };

  const filteredUsers = users.filter(u =>
    u.username.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("fr-FR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit"
    });
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Stats */}
      {usersStats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard icon={Users} label="Total" value={usersStats.total} color="text-white" />
          <StatCard icon={Check} label="Actifs" value={usersStats.active} color="text-emerald-400" />
          <StatCard icon={Shield} label="Admins" value={usersStats.admins} color="text-amber-400" />
          <StatCard icon={User} label="Utilisateurs" value={usersStats.users} color="text-cyan-400" />
        </div>
      )}

      {/* Header + Actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-white flex items-center gap-2">
          <Users className="w-5 h-5 text-amber-500" />
          Gestion des utilisateurs
        </h2>
        <Button
          onClick={() => setShowCreateForm(!showCreateForm)}
          size="sm"
          className="bg-amber-500 hover:bg-amber-600 text-black font-mono text-xs"
        >
          <UserPlus className="w-3.5 h-3.5 mr-1.5" />
          Nouvel utilisateur
        </Button>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <div className="bg-zinc-900/30 backdrop-blur-sm border border-zinc-800/50 rounded-xl p-4 space-y-3">
          <h3 className="text-sm font-medium text-white">Créer un utilisateur</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              placeholder="Nom d'utilisateur"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              className="bg-zinc-800 border-zinc-700 text-white text-sm h-9"
            />
            <div className="relative">
              <Input
                type={showNewPassword ? "text" : "password"}
                placeholder="Mot de passe"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white text-sm h-9 pr-9"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors"
              >
                {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-zinc-400">Rôle :</span>
            <div className="flex gap-2">
              <button
                onClick={() => setNewRole("user")}
                className={`px-3 py-1.5 rounded text-xs font-mono transition-colors ${
                  newRole === "user"
                    ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30"
                    : "bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-zinc-600"
                }`}
              >
                Utilisateur
              </button>
              <button
                onClick={() => setNewRole("admin")}
                className={`px-3 py-1.5 rounded text-xs font-mono transition-colors ${
                  newRole === "admin"
                    ? "bg-amber-500/20 text-amber-400 border border-amber-500/30"
                    : "bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-zinc-600"
                }`}
              >
                Admin
              </button>
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <Button
              onClick={handleCreate}
              disabled={isCreating || !newUsername.trim() || !newPassword.trim()}
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
            >
              {isCreating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1.5" />}
              Créer
            </Button>
            <Button
              onClick={() => { setShowCreateForm(false); setNewUsername(""); setNewPassword(""); setShowNewPassword(false); }}
              variant="ghost"
              size="sm"
              className="text-zinc-400 hover:text-white text-xs"
            >
              Annuler
            </Button>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input
          placeholder="Rechercher un utilisateur..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9 bg-zinc-900 border-zinc-800 text-white text-sm h-9"
        />
      </div>

      {/* Users List */}
      {isLoadingUsers ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-zinc-500 animate-spin" />
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="text-center py-12 text-zinc-500 text-sm">
          {searchQuery ? "Aucun utilisateur trouvé" : "Aucun utilisateur créé"}
        </div>
      ) : (
        <div className="space-y-2">
          {filteredUsers.map((user) => {
            const roleConfig = ROLE_CONFIG[user.role] || ROLE_CONFIG.user;
            const isExpanded = expandedUser === user.id;
            const isEditing = editingUser === user.id;

            const isOnline = user.last_seen && (Date.now() - new Date(user.last_seen).getTime()) < 5 * 60 * 1000;

            return (
              <div
                key={user.id}
                className={`bg-zinc-900 border rounded-lg overflow-hidden transition-colors ${
                  !user.is_active ? "border-red-500/20 opacity-60" : "border-zinc-800"
                }`}
              >
                {/* User Row */}
                <div
                  className={`p-3 sm:p-4 flex items-center gap-3 ${
                    !user.is_main_admin ? "cursor-pointer hover:bg-zinc-800/50 transition-colors" : ""
                  }`}
                  onClick={() => {
                    if (user.is_main_admin) return;
                    if (isExpanded) {
                      setExpandedUser(null);
                      setEditingUser(null);
                    } else {
                      setExpandedUser(user.id);
                    }
                  }}
                >
                  <div className="relative shrink-0">
                    {user.avatar ? (
                      <img
                        src={user.avatar}
                        alt={user.username}
                        className={`w-9 h-9 rounded-full object-cover border ${
                          user.role === "super_admin" ? "border-red-500/30" : user.role === "admin" ? "border-amber-500/30" : "border-cyan-500/30"
                        }`}
                      />
                    ) : (
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center ${
                        user.role === "super_admin" ? "bg-red-500/20" : user.role === "admin" ? "bg-amber-500/20" : "bg-cyan-500/20"
                      }`}>
                        {user.role === "super_admin" ? (
                          <Shield className="w-4 h-4 text-red-400" />
                        ) : user.role === "admin" ? (
                          <Shield className="w-4 h-4 text-amber-400" />
                        ) : (
                          <User className="w-4 h-4 text-cyan-400" />
                        )}
                      </div>
                    )}
                    <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-zinc-900 ${isOnline ? "bg-emerald-500" : "bg-zinc-600"}`} title={isOnline ? "En ligne" : "Hors ligne"} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm text-white font-medium font-mono">{user.username}</span>
                      <Badge className={`${roleConfig.color} text-[10px] px-1.5`}>
                        {roleConfig.label}
                      </Badge>
                      {!user.is_active && (
                        <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-[10px] px-1.5">
                          Désactivé
                        </Badge>
                      )}
                    </div>
                    <p className="text-[11px] text-zinc-600 mt-0.5">
                      {user.is_main_admin ? "Compte principal" : `Créé le ${formatDate(user.created_at)}`}
                      {user.email_ids?.length > 0 && (
                        <span className="ml-2 text-cyan-500/60">
                          <Mail className="w-3 h-3 inline mr-0.5" />{user.email_ids.length} email{user.email_ids.length > 1 ? "s" : ""}
                        </span>
                      )}
                    </p>
                  </div>
                </div>

                {/* Expanded Actions */}
                {isExpanded && (
                  <div className="border-t border-zinc-800 p-3 sm:p-4 space-y-3 bg-zinc-950/50">
                    {isEditing ? (
                      <div className="space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="text-[10px] text-zinc-500 uppercase mb-1 block">Nom d'utilisateur</label>
                            <Input
                              value={editUsername}
                              onChange={(e) => setEditUsername(e.target.value)}
                              className="bg-zinc-800 border-zinc-700 text-white text-sm h-9"
                            />
                          </div>
                          <div>
                            <label className="text-[10px] text-zinc-500 uppercase mb-1 block">Nouveau mot de passe</label>
                            <div className="relative">
                              <Input
                                type={showEditPassword ? "text" : "password"}
                                value={editPassword}
                                onChange={(e) => setEditPassword(e.target.value)}
                                placeholder="Laisser vide pour ne pas changer"
                                className="bg-zinc-800 border-zinc-700 text-white text-sm h-9 pr-9"
                              />
                              <button
                                type="button"
                                onClick={() => setShowEditPassword(!showEditPassword)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors"
                              >
                                {showEditPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>
                        </div>
                        {currentAdminRole === "super_admin" && (
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-zinc-400">Rôle :</span>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setEditRole("user")}
                              className={`px-3 py-1.5 rounded text-xs font-mono transition-colors ${
                                editRole === "user"
                                  ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30"
                                  : "bg-zinc-800 text-zinc-400 border border-zinc-700"
                              }`}
                            >
                              Utilisateur
                            </button>
                            <button
                              onClick={() => setEditRole("admin")}
                              className={`px-3 py-1.5 rounded text-xs font-mono transition-colors ${
                                editRole === "admin"
                                  ? "bg-amber-500/20 text-amber-400 border border-amber-500/30"
                                  : "bg-zinc-800 text-zinc-400 border border-zinc-700"
                              }`}
                            >
                              Admin
                            </button>
                          </div>
                        </div>
                        )}
                        {/* Permissions toggles */}
                        <div className="border border-zinc-800/50 rounded-xl overflow-hidden">
                          <div className="px-3 py-2 bg-zinc-900/50 border-b border-zinc-800/50">
                            <span className="text-[10px] text-zinc-500 uppercase font-medium">Permissions{currentAdminRole !== "super_admin" ? " (lecture seule)" : ""}</span>
                          </div>
                          <div className="divide-y divide-zinc-800/50">
                            {USER_PERMISSIONS.map((perm) => (
                              <div key={perm.key} className="flex items-center justify-between px-3 py-2.5">
                                <div>
                                  <p className="text-sm text-white">{perm.label}</p>
                                  <p className="text-[11px] text-zinc-500">{perm.description}</p>
                                </div>
                                <Switch
                                  checked={editPermissions[perm.key] ?? false}
                                  onCheckedChange={currentAdminRole === "super_admin" ? (checked) =>
                                    setEditPermissions(prev => ({ ...prev, [perm.key]: checked }))
                                  : undefined}
                                  disabled={currentAdminRole !== "super_admin"}
                                  className="data-[state=checked]:bg-emerald-500"
                                />
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Admin-only permissions */}
                        {editRole === "admin" && (
                          <div className="border border-amber-500/20 rounded-lg overflow-hidden">
                            <div className="px-3 py-2 bg-amber-500/5 border-b border-amber-500/20">
                              <span className="text-[10px] text-amber-400 uppercase font-medium">Permissions Admin{currentAdminRole !== "super_admin" ? " (lecture seule)" : ""}</span>
                            </div>
                            <div className="divide-y divide-zinc-800/50">
                              {ADMIN_PERMISSIONS.map((perm) => (
                                <div key={perm.key} className="flex items-center justify-between px-3 py-2.5">
                                  <div>
                                    <p className="text-sm text-white">{perm.label}</p>
                                    <p className="text-[11px] text-zinc-500">{perm.description}</p>
                                  </div>
                                  <Switch
                                    checked={editPermissions[perm.key] ?? false}
                                    onCheckedChange={currentAdminRole === "super_admin" ? (checked) =>
                                      setEditPermissions(prev => ({ ...prev, [perm.key]: checked }))
                                    : undefined}
                                    disabled={currentAdminRole !== "super_admin"}
                                    className="data-[state=checked]:bg-amber-500"
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleUpdate(user.id)}
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                          >
                            <Check className="w-3.5 h-3.5 mr-1" /> Sauvegarder
                          </Button>
                          <Button
                            onClick={() => { setEditingUser(null); setEditPassword(""); setShowEditPassword(false); }}
                            variant="ghost"
                            size="sm"
                            className="text-zinc-400 hover:text-white text-xs"
                          >
                            Annuler
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex flex-wrap gap-2">
                          <Button
                            onClick={() => {
                              setEditingUser(user.id);
                              setEditUsername(user.username);
                              setEditRole(user.role);
                              setEditPassword("");
                              setEditPermissions(user.permissions || (user.role === "admin" ? {
                                can_create_emails: true, can_reply: true,
                                can_permanent: false, can_custom_address: false,
                                can_manage_users: true, can_edit_config: true,
                                can_delete_emails: true, can_customize: true,
                              } : {
                                can_create_emails: true, can_reply: true,
                                can_permanent: false, can_custom_address: false,
                              }));
                            }}
                            variant="outline"
                            size="sm"
                            className="border-zinc-700 text-zinc-400 hover:text-white text-xs"
                          >
                            <Edit3 className="w-3.5 h-3.5 mr-1.5" /> Modifier
                          </Button>
                          <Button
                            onClick={() => managingEmailsFor === user.id ? setManagingEmailsFor(null) : openEmailManager(user)}
                            variant="outline"
                            size="sm"
                            className={`border-zinc-700 text-xs ${
                              managingEmailsFor === user.id
                                ? "text-cyan-400 border-cyan-500/30 bg-cyan-500/5"
                                : "text-cyan-400 hover:text-cyan-300 hover:border-cyan-500/30"
                            }`}
                          >
                            <Link2 className="w-3.5 h-3.5 mr-1.5" /> Gérer les emails
                          </Button>
                          <Button
                            onClick={() => updateUser(user.id, { is_active: !user.is_active })}
                            variant="outline"
                            size="sm"
                            className={`border-zinc-700 text-xs ${
                              user.is_active
                                ? "text-orange-400 hover:text-orange-300 hover:border-orange-500/30"
                                : "text-emerald-400 hover:text-emerald-300 hover:border-emerald-500/30"
                            }`}
                          >
                            {user.is_active ? (
                              <><X className="w-3.5 h-3.5 mr-1.5" /> Désactiver</>
                            ) : (
                              <><Check className="w-3.5 h-3.5 mr-1.5" /> Activer</>
                            )}
                          </Button>
                          <Button
                            onClick={() => handleDelete(user.id, user.username)}
                            variant="outline"
                            size="sm"
                            className="border-zinc-700 text-red-400 hover:text-red-300 hover:border-red-500/30 hover:bg-red-500/5 text-xs"
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Supprimer
                          </Button>
                        </div>

                        {/* Email Assignment Panel */}
                        {managingEmailsFor === user.id && (
                          <div className="border border-cyan-500/20 rounded-lg overflow-hidden">
                            <div className="px-3 py-2 bg-cyan-500/5 border-b border-cyan-500/20 flex items-center justify-between">
                              <span className="text-[10px] text-cyan-400 uppercase font-medium flex items-center gap-1.5">
                                <Mail className="w-3 h-3" /> Emails assignés ({assignedEmails.length})
                              </span>
                            </div>

                            {/* Assigned emails list */}
                            {isLoadingAssigned ? (
                              <div className="flex items-center justify-center py-4">
                                <Loader2 className="w-4 h-4 text-zinc-500 animate-spin" />
                              </div>
                            ) : assignedEmails.length > 0 ? (
                              <div className="divide-y divide-zinc-800/50">
                                {assignedEmails.map((email) => (
                                  <div key={email.id} className="flex items-center justify-between px-3 py-2">
                                    <div className="flex items-center gap-2 min-w-0">
                                      <Mail className="w-3.5 h-3.5 text-cyan-500/60 shrink-0" />
                                      <span className="text-sm text-white font-mono truncate">{email.address}@{email.domain}</span>
                                      <Badge className={`text-[9px] px-1 shrink-0 ${
                                        email.status === "active" ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                                          : email.status === "permanent" ? "bg-purple-500/20 text-purple-400 border-purple-500/30"
                                          : "bg-zinc-500/20 text-zinc-400 border-zinc-500/30"
                                      }`}>
                                        {email.status === "permanent" ? "∞" : email.status === "active" ? "Actif" : "Expiré"}
                                      </Badge>
                                    </div>
                                    <Button
                                      onClick={() => handleUnassign(user.id, email.id)}
                                      variant="ghost"
                                      size="sm"
                                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 px-2 shrink-0"
                                    >
                                      <Unlink className="w-3 h-3 mr-1" />
                                      <span className="text-[10px]">Retirer</span>
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="px-3 py-3 text-center text-zinc-600 text-xs">
                                Aucun email assigné
                              </div>
                            )}

                            {/* Search to assign new emails */}
                            <div className="border-t border-cyan-500/20 p-3 space-y-2">
                              <div className="relative">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
                                <Input
                                  placeholder="Rechercher un email à assigner..."
                                  value={emailSearch}
                                  onChange={(e) => handleEmailSearch(e.target.value)}
                                  className="pl-8 bg-zinc-800 border-zinc-700 text-white text-xs h-8"
                                />
                                {isSearchingEmails && (
                                  <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500 animate-spin" />
                                )}
                              </div>

                              {emailResults.length > 0 && (
                                <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-lg max-h-40 overflow-y-auto">
                                  {emailResults
                                    .filter(e => !assignedEmails.some(a => a.id === e.id))
                                    .map((email) => (
                                    <button
                                      key={email.id}
                                      onClick={() => handleAssign(user.id, email)}
                                      className="w-full flex items-center justify-between px-2.5 py-2 hover:bg-emerald-500/10 transition-colors cursor-pointer group/assign"
                                    >
                                      <div className="flex items-center gap-2 min-w-0">
                                        <Plus className="w-3 h-3 text-emerald-400/50 group-hover/assign:text-emerald-400 transition-colors shrink-0" />
                                        <span className="text-xs text-zinc-300 group-hover/assign:text-white font-mono truncate transition-colors">{email.address}@{email.domain}</span>
                                        <Badge className={`text-[9px] px-1 shrink-0 ${
                                          email.status === "active" ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                                            : email.status === "permanent" ? "bg-purple-500/20 text-purple-400 border-purple-500/30"
                                            : "bg-zinc-500/20 text-zinc-400 border-zinc-500/30"
                                        }`}>
                                          {email.status === "permanent" ? "∞" : email.status === "active" ? "Actif" : "Expiré"}
                                        </Badge>
                                      </div>
                                      <span className="text-[10px] text-emerald-400/50 group-hover/assign:text-emerald-400 transition-colors shrink-0">Assigner</span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
